import { useCallback, useState } from "react";

import toast from "react-hot-toast";

import giphy, { GiphyGifData } from "simplydo/core/api/giphy";
import { useThrottle } from "simplydo/react/hooks";
import { styled } from "simplydo/react/styled";
import { Button, Icon, Loader, Popup } from "simplydo/react/ui";
import { Input } from "simplydo/react/ui";

import { GiphyGif } from "./Gif";
import GiphyLogo from "./GiphyLogo.png";

const GiphyContainer = styled.div`
  padding: 5px;
  min-width: ${({ theme }) => (theme.sizes.isMobile ? 220 : 660)}px;
  min-height: 360px;
  .nav-buttons {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 10px 0 10px 0;
  }
`;

const GiphyGallery = styled.div`
  display: flex;
  width: ${({ theme }) => (theme.sizes.isMobile ? 220 : 660)}px;
  flex-wrap: wrap;
`;

const GiphyGifContainer = styled.div`
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 5px;
  width: 100px;
  height: 125px;
  overflow: hidden;
  background-color: #e9e9e9;
`;

type GiphyPickerProps = {
  trigger: React.ReactNode;
  open: boolean;
  onChoose: (gif: GiphyGifData) => void;
  onClose: () => void;
};

export const GiphyPicker = ({
  trigger,
  open,
  onChoose,
  onClose,
}: GiphyPickerProps) => {
  const [loading, setLoading] = useState(false);
  const [gifs, setGifs] = useState<GiphyGifData[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);

  const getTrending = useCallback(() => {
    setLoading(true);
    giphy.trending(
      { page: page },
      ({ data }) => {
        setLoading(false);
        setGifs(data);
      },
      (err) => {
        setLoading(false);
        toast.error(err.message);
      },
    );
  }, [page]);

  const searchGifs = useThrottle(
    () => {
      if (searchTerm) {
        setLoading(true);
        giphy.search(
          { query: searchTerm, page: page },
          ({ data }) => {
            setLoading(false);
            setGifs(data);
          },
          (err) => {
            setLoading(false);
            toast.error(err.message);
          },
        );
      } else {
        getTrending();
      }
    },
    500,
    [getTrending, searchTerm, page],
  );

  const handleSearch = useCallback((term: string) => setSearchTerm(term), []);
  const handleClick = useCallback(
    (gif: GiphyGifData) => onChoose(gif),
    [onChoose],
  );

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") searchGifs();
    },
    [searchGifs],
  );

  const pageBack = useCallback(() => {
    setPage((prevPage) => prevPage - 1);
    getTrending();
  }, [getTrending]);

  const pageForward = useCallback(() => {
    setPage((prevPage) => prevPage + 1);
    getTrending();
  }, [getTrending]);

  const searchPageBack = useCallback(() => {
    setPage((prevPage) => prevPage - 1);
    searchGifs();
  }, [searchGifs]);

  const searchPageForward = useCallback(() => {
    setPage((prevPage) => prevPage + 1);
    searchGifs();
  }, [searchGifs]);

  return (
    <div>
      <Popup
        open={open}
        onClose={onClose}
        trigger={<div>{trigger}</div>}
        content={
          <GiphyContainer>
            <Input
              placeholder="Search GIFs..."
              value={searchTerm}
              fluid
              onKeyPress={handleKeyPress}
              onChange={(_e, { value }) => handleSearch(value)}
              style={{ marginBottom: 5 }}
              label={
                <Button
                  content="Search"
                  onClick={() => {
                    setPage(1);
                    searchGifs();
                  }}
                />
              }
              labelPosition="right"
            />
            <div>
              {loading ?
                <Loader active />
              : <GiphyGallery>
                  {gifs.map((gif, i) => (
                    <Popup
                      key={i}
                      trigger={
                        <GiphyGifContainer onClick={() => handleClick(gif)}>
                          <GiphyGif gif={gif} size="fixed_small" key={i} />
                        </GiphyGifContainer>
                      }
                      content={gif.title}
                    />
                  ))}
                </GiphyGallery>
              }
            </div>
            <div className="nav-buttons">
              <Button
                icon
                onClick={searchTerm ? searchPageBack : pageBack}
                labelPosition="left"
                disabled={page === 1}
              >
                <Icon name="arrow circle left" />
                Previous
              </Button>
              <span>Page {page}</span>
              <Button
                icon
                onClick={searchTerm ? searchPageForward : pageForward}
                labelPosition="right"
              >
                Next
                <Icon name="arrow circle right" />
              </Button>
            </div>
            <img
              src={GiphyLogo}
              style={{ float: "right" }}
              alt="Powered by Giphy"
            />
          </GiphyContainer>
        }
        on="click"
        onOpen={() => getTrending()}
      />
    </div>
  );
};
